import { BNBox, BNStack, dateFormatter } from "@bn-monorepo/js-packages";
import { ArticleSettings } from "../../SectionPage";
import { AutorenewOutlined, EventOutlined, ScheduleOutlined } from "@mui/icons-material";
import { PublishElementInfo } from "./PublishElementInfo";
import { AuthorByline } from "./AuthorByline";
import { useContext } from "react";
import { SettingsContext } from "../../../../../settingsContext";
import { Typography } from "@mui/material";
import Media from "../../../../CmsComponents/Media/Media";
import SectionPageContext from "../../SectionPageContext";

interface Props {
  articleSettings?: ArticleSettings;
}

export const PublishInfo: React.FC<Props> = ({ articleSettings }) => {
  const { locale, localization } = useContext(SettingsContext);
  const { sectionImageBorderRadiusOverride, sectionImageWidthOverride } = useContext(SectionPageContext);

  if (!articleSettings) {
    return null;
  }
  const {
    image,
    heading,
    author,
    createDate,
    updateDate,
    displayDates,
    displayEstimatedReadTime,
    numberOfWordsToRead,
  } = articleSettings;

  const date = dateFormatter(locale);
  const formattedCreateDate = date(createDate);
  const formattedUpdateDate = date(updateDate);
  const readingTimeInMinutes = (numberOfWordsToRead?: number) => {
    if (!numberOfWordsToRead || numberOfWordsToRead <= 0) {
      return "";
    }
    // Medium: estimates from total word count using ~265–275 wpm (we use midpoint 270) then rounds to minutes.
    const WORDS_PER_MINUTE = 270;
    const minutesFloat = numberOfWordsToRead / WORDS_PER_MINUTE;
    const minutesRounded = Math.max(1, Math.round(minutesFloat));
    return `${minutesRounded} ${
      minutesRounded === 1 ? localization.publishInfo_ReadTimeSingle : localization.publishInfo_ReadTimePlural
    }`;
  };
  const showUpdatedDate = displayDates && formattedCreateDate != formattedUpdateDate;
  const showReadTime = displayEstimatedReadTime && !!numberOfWordsToRead;
  const showMetaRow = displayDates || showReadTime;
  return (
    <BNBox sx={{ mt: { xs: 2, md: 3 } }}>
      {image && (
        <BNBox
          sx={{
            textAlign: "center",
          }}
        >
          <Media
            AltText={image.alt}
            imagePath={image.url}
            styleOverride={{
              width: sectionImageWidthOverride ?? undefined,
              borderRadius: sectionImageBorderRadiusOverride ?? undefined,
            }}
            noWrapper={true}
            width={image.width}
            height={image.height}
            sx={{
              width: "32rem",
            }}
          />
        </BNBox>
      )}
      {heading && (
        <Typography
          sx={{ fontWeight: "bold", mt: { xs: 2, md: 3 }, fontSize: { xs: "2.8rem", md: "3.2rem" } }}
          variant="h4"
          component={"h1"}
        >
          {heading}
        </Typography>
      )}
      <AuthorByline author={author} />
      {showMetaRow && (
        <BNStack
          direction={"row"}
          sx={{
            mt: 2,
            flexWrap: "wrap",
            columnGap: 2,
            rowGap: 1.5,
          }}
          data-uit="article-publish-meta"
        >
          {displayDates && (
            <PublishElementInfo
              Icon={EventOutlined}
              label={localization.publishInfo_Published}
              value={formattedCreateDate}
            />
          )}
          {showUpdatedDate && (
            <PublishElementInfo
              Icon={AutorenewOutlined}
              label={localization.publishInfo_Updated}
              value={formattedUpdateDate}
            />
          )}
          {showReadTime && (
            <PublishElementInfo
              Icon={ScheduleOutlined}
              label={localization.publishInfo_ReadTime}
              value={readingTimeInMinutes(numberOfWordsToRead)}
            />
          )}
        </BNStack>
      )}
    </BNBox>
  );
};
